// components/three/obstacles/ObstacleProvider.jsx
'use client'

import { createContext, useContext, useMemo } from 'react'
import { useGLTF } from '@react-three/drei'
import { parseObstacles, collidersFromBlocks, walkablesFromFloors } from '../../../lib/obstacles/loadObstacles'
import { wantBlocks } from '../../../lib/world/wants'
import { monorailBlocks, monorailBlockHeight, monorailStationSolids, monorailStationFloors, monorailBeamFloors, monorailVisitorSolids, monorailStationVisitorParts } from '../../../lib/world/monorail'
import { laundretteBlocks, LAUNDRETTE_HEIGHT } from '../../../lib/world/laundrette'

// Parsed once per GLB scene. Same reason as the terrain provider: a
// suspended render loses its useMemo, and this parse (133 boxes, each a
// world-matrix update and a bounding box) ran seventeen times on a cold
// load before the tree committed.
const _parsed = new WeakMap()
const NONE = { solids: [], floors: [] }

/**
 * @typedef {Object} ObstacleContextValue
 * @property {import('../../../lib/obstacles/loadObstacles').AABBCollider[]} obstacles
 * @property {import('../../../lib/obstacles/loadObstacles').AABBCollider[]} noGoZones
 * @property {import('../../../lib/obstacles/loadObstacles').AABBCollider[]} walkables
 * @property {{ solids: import('../../../lib/obstacles/loadObstacles').AABBCollider[], floors: import('../../../lib/obstacles/loadObstacles').AABBCollider[] }} visitorOnly
 * @property {boolean} isLoaded
 */

const ObstacleContext = createContext(/** @type {ObstacleContextValue | null} */ (null))

/**
 * Provides parsed obstacle colliders from the obstacles.glb file.
 * Wrap your scene components that need obstacle data inside this provider.
 * 
 * @param {Object} props
 * @param {string} [props.modelPath='/models/obstacles.glb'] - Path to obstacles GLB
 * @param {React.ReactNode} props.children
 */
export function ObstacleProvider({ modelPath = '/models/obstacles.glb', children }) {
  const { scene } = useGLTF(modelPath)

  const colliders = useMemo(() => {
    if (!scene) return { obstacles: [], noGoZones: [], walkables: [], visitorOnly: NONE, isLoaded: false }
    if (_parsed.has(scene)) return _parsed.get(scene)

    const { obstacles, noGoZones, walkables } = parseObstacles(scene)
    // The things he asked for that stand in the town (lib/world/wants.js):
    // a stall or a stage is something he walks round, not through. And the
    // monorail's pylons (lib/world/monorail.js), as tall as the tallest, so
    // the chase camera keeps off them too; and its lift tower and the walls
    // round the platform it goes up to, each as high as it stands, with the
    // platform and its steps to stand on. The town is sent `obstacles`.
    // `visitorOnly` is for a visitor's feet and nobody else's: the beams'
    // tops, to walk along (Sam, 3 Nov), the walls that stop them in the
    // tunnels, and the station's far platform and trackbed. He is drawn on
    // `walkables`, so never up on a beam. And the laundrette's walls
    // (lib/world/laundrette.js), as tall as its roof, so the chase camera
    // keeps out of them too
    const far = monorailStationVisitorParts()
    const out = {
      obstacles: [...obstacles, ...collidersFromBlocks(wantBlocks()), ...collidersFromBlocks(monorailBlocks(), monorailBlockHeight()), ...collidersFromBlocks(monorailStationSolids()),
        ...collidersFromBlocks(laundretteBlocks(), LAUNDRETTE_HEIGHT)],
      noGoZones,
      walkables: [...walkables, ...walkablesFromFloors(monorailStationFloors())],
      visitorOnly: {
        solids: collidersFromBlocks([...monorailVisitorSolids(), ...far.solids]),
        floors: walkablesFromFloors([...monorailBeamFloors(), ...far.floors]),
      },
      isLoaded: true,
    }
    _parsed.set(scene, out)
    return out
  }, [scene])

  return (
    <ObstacleContext.Provider value={colliders}>
      {children}
    </ObstacleContext.Provider>
  )
}

/**
 * Hook to access obstacle colliders.
 * Must be used within an ObstacleProvider.
 * 
 * @returns {ObstacleContextValue}
 */
export function useObstacles() {
  const ctx = useContext(ObstacleContext)
  if (!ctx) {
    console.warn('[useObstacles] Used outside of ObstacleProvider, returning empty colliders')
    return { obstacles: [], noGoZones: [], walkables: [], visitorOnly: NONE, isLoaded: false }
  }
  return ctx
}

// Preload helper for performance
useGLTF.preload('/models/obstacles.glb')

